import type {
  EventHandlerCb,
  ExtendedConstraints,
  CapabilityConfig,
} from '../../types';
import { hasOnlyKeysFromSet } from '../../utils';
import { FronteggExtensionsFeatureKeys } from '../../../entitlements/fronteggExtensionsKeys';

export const id = 'app-token';

export enum Events {
  GetStatus = 'app-token:get-status',
  /**
   * @deprecated will be removed soon, please use app-token:get-status event and its successful payload.
   */
  Update = 'app-token:update',
}

export enum Actions {
  Get = 'app-token:get',
}

/**
 * Config for the `app-token` capability.
 *
 * `features` declares which Frontegg feature entitlement keys this app wants
 * resolved into the JWT's `features` claim. Token generation queries Frontegg
 * for each declared key and embeds only the entitled subset into the token.
 *
 * Invariant (only relevant for apps with an API resolver gated on
 * token features): `features` must include every key the app's API
 * resolver checks for. Otherwise the JWT will lack the key and the resolver
 * will reject the request. Apps without a backend can declare
 * any subset, including empty.
 *
 * Each value must be a member of `FronteggExtensionsFeatureKeys` (the shared
 * SDK enum); only those keys are retrievable through this capability. Manifests
 * containing unknown keys fail validation.
 */
export type Config = CapabilityConfig & {
  type: typeof id;
  features?: FronteggExtensionsFeatureKeys[];
};
/**
 * Stable error codes returned on `GetStatusPayload` failures so consumers can
 * distinguish failure populations in telemetry without parsing free-text messages.
 */
export enum ErrorCode {
  HostUnauthenticated = 'HOST_UNAUTHENTICATED',
}

/**
 * @deprecated UpdatePayload will be removed with update event.
 */
export type UpdatePayload = string;
export type GetStatusPayload =
  | { isSuccess: true; data: string }
  | { isSuccess: false; errorMessage: string; code?: ErrorCode };

/**
 * @deprecated GetPayload will be removed from get event.
 */
export type GetPayload = {
  appName: string;
};

export type Constraints = ExtendedConstraints<{
  events: Events;
  actions: Actions;
  config: Config;
  eventPayloadTuple:
    | [Events.Update, UpdatePayload]
    | [Events.GetStatus, GetStatusPayload];
  eventHandlerTuple:
    | [Events.Update, EventHandlerCb<UpdatePayload>]
    | [Events.GetStatus, EventHandlerCb<GetStatusPayload>];
  actionPayloadTuple: [Actions.Get] | [Actions.Get, GetPayload];
}>;

const VALID_CONFIG_KEYS = ['type', 'features'] as const;
const VALID_FEATURE_KEYS: ReadonlySet<string> = new Set(
  Object.values(FronteggExtensionsFeatureKeys),
);

export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  const { features } = config;
  if (features !== undefined) {
    if (
      !Array.isArray(features) ||
      !features.every((f) => typeof f === 'string' && VALID_FEATURE_KEYS.has(f))
    ) {
      return false;
    }
  }

  return true;
}
